import React, { useState, useEffect, useRef, useMemo } from "react";
import Layout from "../Layout/Layout";
import { ChatSend, ChatReceive } from "../Components/Chat/ChatBody";
import { MessageBox } from "../Components/Chat/ChatInput";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../actions/api";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import useUserStore from "../zustand-stores/UserStore";
import { useQueryClient } from "@tanstack/react-query";
import { reOrderTargets } from "../helpers/GoalsAndTargets/BodyTentChatHelper";
import { buildGoalsTargetsDraftScopeId } from "./GoalsAndTargets";

export default function BodyTentChat() {
  const { user: userData } = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { goalId, goalDetailId, today, activeTabIndex, weekStart } =
    location.state || {};

  // Patch
  const queryClient = useQueryClient();

  const [userGoal, setUserGoal] = useState([]);
  const [userGoalLoading, setUserGoalLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [mainQuestions, setMainQuestions] = useState([]);
  const [isJournalStarted, setIsJournalStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [messages, setMessages] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isMessageSent, setIsMessageSent] = useState(false);
  const [targets, setTargets] = useState([]);

  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const [currentYesNoQuestion, setCurrentYesNoQuestion] = useState(null);
  const [isThinkingLoading, setIsThinkingLoading] = useState(false);
  const [noQuestions, setNoQuestions] = useState(false);

  const [messageSending, setMessageSending] = useState(false);

  const chatContainerRef = useRef(null);

  const weekIsoForDraft = useMemo(() => {
    if (weekStart) return weekStart;
    if (today) return new Date(today).toISOString();
    return undefined;
  }, [weekStart, today]);

  const tentComposerDraftScopeId = useMemo(() => {
    if (editingQuestion) return undefined;
    return buildGoalsTargetsDraftScopeId({
      goalId,
      goalDetailId,
      weekStartIso: weekIsoForDraft,
      userGoalId: userGoal?.id,
      questionId: currentQuestion?.id,
      isFollowUp: !!currentQuestion?.isFollowUp,
    });
  }, [
    editingQuestion,
    goalId,
    goalDetailId,
    weekIsoForDraft,
    userGoal?.id,
    currentQuestion?.id,
    currentQuestion?.isFollowUp,
  ]);

  const fetchOrCreateUserGoal = async (forminit = false) => {
    try {
      // Format today date for API if provided
      const todayFormatted = today
        ? `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`
        : null;

      const res = await api.post("/user/targets/add", {
        goal_id: goalId,
        goal_detail_id: goalDetailId,
        ...(todayFormatted && { today: todayFormatted }),
      });

      const fetchedQuestions = res.data.data.questions;
      const fetchedMainQuestions = res.data.data.mainQuestions || [];

      if (fetchedQuestions.length === 0 && fetchedMainQuestions.length === 0) {
        setNoQuestions(true);
        setUserGoal(res.data.data.user_goal);
        return;
      }

      setUserGoal(res.data.data.user_goal);
      setQuestions(fetchedQuestions);
      setMainQuestions(fetchedMainQuestions);

      setTargets(reOrderTargets(res.data.data.current_user_targets) || []);

      // Initialize chat based on questions
      initializeChat(fetchedMainQuestions, fetchedQuestions, forminit);
    } catch (err) {
      console.error("Failed to add goal target", err);
      toast.error("Failed to add targets.");
      navigate("/goals-and-targets", {
        replace: true,
        state: {
          activeTabIndex,
          weekStart,
        },
      });
    } finally {
    }
  };

  const processFollowUpQuestions = (
    mainQuestion,
    chatMessages,
    nextQuestionRef,
  ) => {
    if (!mainQuestion.follow_up_questions) return;

    for (const followUp of mainQuestion.follow_up_questions) {
      // Only add answered follow-up questions to chat
      if (
        followUp.answered &&
        followUp.answers &&
        followUp.answers.length > 0
      ) {
        chatMessages.push({
          type: "send",
          question: followUp.question,
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
          questionId: followUp.id,
          questionType: followUp.question_type || "text_input",
        });

        const answer = followUp.answers[0];
        chatMessages.push({
          type: "receive",
          response: answer.text,
          time: moment(answer.created_at).format("h:mm A"),
          responder: userData?.full_name || "User",
          userImage: userData?.image || "/icons/toni.svg",
          answerId: answer.id,
          questionType: followUp.question_type || "text_input",
        });
      } else {
        // Set the first unanswered follow-up as current question
        if (!nextQuestionRef.current) {
          nextQuestionRef.current = {
            ...followUp,
            isFollowUp: true,
            mainQuestionId: mainQuestion.id,
          };
        }
        // Stop processing further follow-ups once we find an unanswered one
        break;
      }
    }
  };

  const processOldQuestions = (
    questionsData,
    chatMessages,
    startIndex = 0,
    nextQuestionRef,
  ) => {
    for (let i = startIndex; i < questionsData.length; i++) {
      const question = questionsData[i];

      if (question.answered && question.answer && question.answer.length > 0) {
        chatMessages.push({
          type: "send",
          question: question.question_text,
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
          questionId: question.id,
          questionType: "text_input",
        });

        const answer = question.answer[0];
        chatMessages.push({
          type: "receive",
          response: answer.text,
          time: moment(answer.created_at).format("h:mm A"),
          responder: userData?.full_name || "User",
          userImage: userData?.image || "/icons/toni.svg",
          answerId: answer.id,
          questionType: "text_input",
        });
      } else {
        // Set as current question if not answered and add to chat
        if (!nextQuestionRef.current) {
          chatMessages.push({
            type: "send",
            question: question.question_text,
            time: moment().format("h:mm A"),
            botImage: "/icons/journal-brave.svg",
            questionId: question.id,
            questionType: "text_input",
          });
          nextQuestionRef.current = { ...question, isOldQuestion: true };
        }
        break;
      }
    }
  };

  const processMainQuestionsSequentially = (
    mainQuestionsData,
    chatMessages,
    nextQuestionRef,
    startIndex = 0,
  ) => {
    for (let i = startIndex; i < mainQuestionsData.length; i++) {
      const mainQuestion = mainQuestionsData[i];

      // Always add the main question to chat first
      chatMessages.push({
        type: "send",
        question: mainQuestion.question,
        time: moment().format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
        questionId: mainQuestion.id,
        questionType: mainQuestion.question_type || "text_input",
      });

      if (
        mainQuestion.answered &&
        mainQuestion.answers &&
        mainQuestion.answers.length > 0
      ) {
        // Add answered main question response to chat
        const answer = mainQuestion.answers[0];
        chatMessages.push({
          type: "receive",
          response: answer.text,
          time: moment(answer.created_at).format("h:mm A"),
          responder: userData?.full_name || "User",
          userImage: userData?.image || "/icons/toni.svg",
          answerId: answer.id,
          questionType: mainQuestion.question_type || "text_input",
        });

        // Process follow-up questions for this main question
        processFollowUpQuestions(mainQuestion, chatMessages, nextQuestionRef);

        // If there's an unanswered follow-up, stop processing
        if (nextQuestionRef.current && nextQuestionRef.current.isFollowUp) {
          // Add the unanswered follow-up question to chat
          chatMessages.push({
            type: "send",
            question: nextQuestionRef.current.question,
            time: moment().format("h:mm A"),
            botImage: "/icons/journal-brave.svg",
            questionId: nextQuestionRef.current.id,
            questionType: nextQuestionRef.current.question_type || "text_input",
          });
          break;
        }
      } else {
        // Set unanswered main question as current (already added to chat above)
        nextQuestionRef.current = { ...mainQuestion, isFollowUp: false };
        break;
      }
    }
  };

  const initializeChat = (mainQuestionsData, questionsData, frominit) => {
    const chatMessages = [];
    let hasAnyAnswers = false;
    const nextQuestionRef = { current: null };

    // Check if there are any answered questions to determine if journal is started
    const hasAnsweredMainQuestions = mainQuestionsData.some(
      (mq) =>
        mq.answered ||
        (mq.follow_up_questions &&
          mq.follow_up_questions.some((fq) => fq.answered)),
    );
    const hasAnsweredOldQuestions = questionsData.some((q) => q.answered);
    hasAnyAnswers = hasAnsweredMainQuestions || hasAnsweredOldQuestions;

    if (mainQuestionsData && mainQuestionsData.length > 0) {
      // Process all main questions sequentially (starting from index 0)
      processMainQuestionsSequentially(
        mainQuestionsData,
        chatMessages,
        nextQuestionRef,
        0,
      );

      // If all main questions are answered, process old questions
      if (
        !nextQuestionRef.current &&
        questionsData &&
        questionsData.length > 0
      ) {
        const allMainAnswered = mainQuestionsData.every(
          (mq) =>
            mq.answered &&
            (!mq.follow_up_questions ||
              mq.follow_up_questions.every((fq) => fq.answered)),
        );

        if (allMainAnswered) {
          processOldQuestions(questionsData, chatMessages, 0, nextQuestionRef);
        }
      }
    } else if (questionsData && questionsData.length > 0) {
      // Handle old questions format
      processOldQuestions(questionsData, chatMessages, 0, nextQuestionRef);
    }

    if (nextQuestionRef.current) {
      if (nextQuestionRef.current.question_type === "yes_no") {
        setCurrentYesNoQuestion(nextQuestionRef.current);
        setShowYesNoModal(true);
        setCurrentQuestion(null);
      } else {
        setCurrentQuestion(nextQuestionRef.current);
      }
    }

    // Update state
    setMessages(chatMessages);
    setIsJournalStarted(hasAnyAnswers);

    // Check if all questions are answered
    if (!frominit) {
      checkAllQuestionsAnswered(mainQuestionsData, questionsData);
    }
  };

  const checkAllQuestionsAnswered = (mainQuestionsData, questionsData) => {
    let allAnswered = true;

    // Check main questions
    if (mainQuestionsData && mainQuestionsData.length > 0) {
      for (const mainQ of mainQuestionsData) {
        if (!mainQ.answered) {
          allAnswered = false;
          break;
        }

        // Check follow-up questions
        if (mainQ.follow_up_questions) {
          for (const followUp of mainQ.follow_up_questions) {
            if (!followUp.answered) {
              allAnswered = false;
              break;
            }
          }
        }

        if (!allAnswered) break;
      }
    }

    // Check old questions
    if (allAnswered && questionsData && questionsData.length > 0) {
      allAnswered = questionsData.every((q) => q.answered);
    }

    setAllQuestionsAnswered(allAnswered);

    if (
      allAnswered &&
      (mainQuestionsData.length > 0 || questionsData.length > 0)
    ) {
      handleGoalCompletion();
    }
  };

  const handleGoalCompletion = () => {
    setAllQuestionsAnswered(true);
    setCurrentQuestion(null);

    queryClient.invalidateQueries({ queryKey: ["goalsAndTargets", "goals"] });
    queryClient.invalidateQueries({
      queryKey: ["goalsAndTargets", "reflections"],
    });

    setMessages((prev) => [
      ...prev,
      {
        type: "send",
        question: "Thank you for setting up your targets.",
        time: moment().format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
      },
    ]);

    toast.success("Targets set successfully");
    navigate("/goals-and-targets", {
      state: { activeTabIndex, weekStart },
    });
  };

  const findNextUnansweredQuestion = () => {
    // Check main questions first
    if (mainQuestions && mainQuestions.length > 0) {
      for (const mainQ of mainQuestions) {
        if (!mainQ.answered) {
          return { ...mainQ, isFollowUp: false };
        }

        // Check follow-up questions
        if (mainQ.follow_up_questions) {
          for (const followUp of mainQ.follow_up_questions) {
            if (!followUp.answered) {
              return {
                ...followUp,
                isFollowUp: true,
                mainQuestionId: mainQ.id,
              };
            }
          }
        }
      }
    }

    // Check old questions
    if (questions && questions.length > 0) {
      const unanswered = questions.find((q) => !q.answered);
      if (unanswered) {
        return { ...unanswered, isOldQuestion: true };
      }
    }

    return null;
  };

  useEffect(() => {
    if (goalId && goalDetailId) {
      setUserGoalLoading(true);
      fetchOrCreateUserGoal(true);
      setUserGoalLoading(false);
    } else {
      toast.error("Invalid target data.");
      navigate("/goals-and-targets", {
        state: { activeTabIndex, weekStart },
      });
    }
  }, [goalId, goalDetailId, navigate]);

  const handleJournalingPress = () => {
    setIsJournalStarted(true);

    // Find the next unanswered question
    const nextQuestion = findNextUnansweredQuestion();

    if (nextQuestion) {
      if (nextQuestion.question_type === "yes_no") {
        setCurrentYesNoQuestion(nextQuestion);
        setShowYesNoModal(true);
      } else {
        setCurrentQuestion(nextQuestion);
      }
    }
  };

  const handleStartEdit = (answerId, currentText) => {
    if (!answerId) {
      console.error("No answer ID provided for edit");
      toast.error("Cannot edit this message - missing ID");
      return;
    }

    let messageToEdit = null;
    let questionType = "text_input";

    // Find the message to edit in main questions
    for (const mainQ of mainQuestions) {
      if (mainQ.answers && mainQ.answers.find((a) => a.id === answerId)) {
        messageToEdit = mainQ;
        questionType = mainQ.question_type || "text_input";
        break;
      }

      if (mainQ.follow_up_questions) {
        for (const followUpQ of mainQ.follow_up_questions) {
          if (
            followUpQ.answers &&
            followUpQ.answers.find((a) => a.id === answerId)
          ) {
            messageToEdit = followUpQ;
            questionType = followUpQ.question_type;
            break;
          }
        }
      }
      if (messageToEdit) break;
    }

    // If not found, check old questions
    if (!messageToEdit) {
      messageToEdit = questions.find(
        (q) =>
          q.answer && q.answer.find && q.answer.find((a) => a.id === answerId),
      );
    }

    if (!messageToEdit) {
      console.error("Message to edit not found");
      toast.error("Cannot find the message to edit");
      return;
    }

    if (questionType === "yes_no") {
      if (mainQuestions.length > 0) {
        let questionToEdit = null;

        for (const mainQ of mainQuestions) {
          if (mainQ.answers && mainQ.answers.find((a) => a.id === answerId)) {
            questionToEdit = { ...mainQ, isFollowUp: false };
            break;
          }

          if (mainQ.follow_up_questions) {
            for (const followUpQ of mainQ.follow_up_questions) {
              if (
                followUpQ.answers &&
                followUpQ.answers.find((a) => a.id === answerId)
              ) {
                questionToEdit = {
                  ...followUpQ,
                  isFollowUp: true,
                  mainQuestionId: mainQ.id,
                };
                break;
              }
            }
          }
          if (questionToEdit) break;
        }

        if (questionToEdit) {
          setCurrentYesNoQuestion(questionToEdit);
          setShowYesNoModal(true);
          setEditingQuestion({
            answerId,
            currentText,
            isYesNo: true,
          });
        } else {
          toast.error("Could not find the question to edit");
        }
      }
    } else {
      setEditingQuestion({ answerId, currentText });
    }
  };

  const handleSaveEdit = async (answerId, newText) => {
    if (!answerId) {
      console.error("No answer ID provided for edit");
      toast.error("Cannot save edit - missing message ID");
      return;
    }

    setMessageSending(true);
    try {
      let response;

      let isMainOrFollowUp = false;

      for (const mainQ of mainQuestions) {
        if (mainQ.answers?.find((a) => a.id === answerId)) {
          isMainOrFollowUp = true;
          break;
        }
        if (mainQ.follow_up_questions) {
          for (const followUpQ of mainQ.follow_up_questions) {
            if (followUpQ.answers?.find((a) => a.id === answerId)) {
              isMainOrFollowUp = true;
              break;
            }
          }
        }
        if (isMainOrFollowUp) break;
      }

      if (isMainOrFollowUp) {
        response = await api.post(`/user/target/answer/main/${answerId}`, {
          text: newText.trim(),
        });
      } else {
        response = await api.post(`/user/target/answer/${answerId}`, {
          text: newText.trim(),
        });
      }

      toast.success(response.data.message || "Answer updated successfully");
      setEditingQuestion(null);
      await fetchOrCreateUserGoal(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update answer. Please try again.";
      toast.error(errorMessage);
      console.error("Failed to update answer:", error);
    } finally {
      setMessageSending(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
  };

  const handleSendMessage = async (messageText) => {
    // Handle edit mode
    if (editingQuestion && !editingQuestion.isYesNo) {
      await handleSaveEdit(editingQuestion.answerId, messageText);
      return;
    }

    if (!currentQuestion || !messageText.trim()) return;

    if (currentQuestion.question_type === "yes_no") {
      return;
    }

    setMessageSending(true);
    const currentTime = moment().format("h:mm A");

    const newUserMessage = {
      type: "receive",
      response: messageText.trim(),
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
    };

    setMessages((prev) => [...prev, newUserMessage]);

    try {
      let res;

      if (currentQuestion.isOldQuestion) {
        res = await api.post("/user/target/answer", {
          user_goal_id: userGoal.id,
          question_id: currentQuestion.id,
          text: messageText.trim(),
        });
      } else if (mainQuestions && mainQuestions.length > 0) {
        const payload = {
          user_goal_id: userGoal.id,
          text: messageText.trim(),
        };

        if (currentQuestion.isFollowUp) {
          payload.follow_up_question_id = currentQuestion.id;
        } else {
          payload.main_question_id = currentQuestion.id;
        }

        res = await api.post("/user/target/answer/main", payload);
      } else {
        res = await api.post("/user/target/answer", {
          user_goal_id: userGoal.id,
          question_id: currentQuestion.id,
          text: messageText.trim(),
        });
      }

      setIsJournalStarted(true);

      // Refetch data to get updated questions and find next unanswered
      setIsThinkingLoading(true);
      await fetchOrCreateUserGoal();
      // setTimeout(() => {
      setIsThinkingLoading(false);
      // }, 1000);
    } catch (err) {
      console.error("Failed to send message", err);
      toast.error("Failed to send message.");

      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setMessageSending(false);
    }
  };

  const handleYesNoResponse = async (response) => {
    const messageText = response ? "yes" : "no";
    const currentTime = moment().format("h:mm A");

    const isEditing = editingQuestion?.isYesNo;
    const answerId = isEditing ? editingQuestion.answerId : Date.now();

    const updatedMessages = [...messages];
    const messageIndex = isEditing
      ? updatedMessages.findIndex(
          (msg) => msg.type === "receive" && msg.answerId === answerId,
        )
      : -1;

    const newUserMessage = {
      type: "receive",
      response: messageText,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
      answerId,
      questionType: currentYesNoQuestion.question_type,
    };

    if (messageIndex !== -1) {
      updatedMessages[messageIndex] = newUserMessage;
    } else {
      updatedMessages.push(newUserMessage);
    }

    setMessages(updatedMessages);
    setShowYesNoModal(false);
    if (isEditing) setEditingQuestion(null);

    setMessageSending(true);
    try {
      if (isEditing) {
        await api.post(`/user/target/answer/main/${answerId}`, {
          text: messageText,
        });
        await fetchOrCreateUserGoal(true);
      } else {
        const payload = {
          user_goal_id: userGoal.id,
          text: messageText,
        };

        if (currentYesNoQuestion.isFollowUp) {
          payload.follow_up_question_id = currentYesNoQuestion.id;
        } else {
          payload.main_question_id = currentYesNoQuestion.id;
        }

        await api.post("/user/target/answer/main", payload);
        setIsJournalStarted(true);

        // Refetch and find next question
        setIsThinkingLoading(true);
        await fetchOrCreateUserGoal();
        // setTimeout(() => {
        setIsThinkingLoading(false);
      }

      // }, 1000);
    } catch (err) {
      console.error("Error submitting yes/no answer:", err);
      toast.error("Error submitting yes/no answer.");

      // Revert changes on error
      if (isEditing) {
        setMessages(messages);
      } else {
        setMessages((prev) => prev.slice(0, -1));
      }

      setShowYesNoModal(true);
    } finally {
      setMessageSending(false);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isMessageSent]);

  const renderChat = () => {
    return (
      <div className="d-flex flex-column goals-chat-card-container">
        <div
          ref={chatContainerRef}
          className="d-flex flex-column no-scrollbar"
          style={{
            flexGrow: 1,
            overflowX: "hidden",
            minHeight: 0,
            height: 0,
            flexShrink: 0,
          }}
        >
          {/* Render all messages */}
          {messages.map((message, index) => {
            if (message.type === "send") {
              const sendIndex = messages
                .slice(0, index + 1)
                .filter((m) => m.type === "send").length;

              const isSecondSend = sendIndex === 2; // second 'send' message

              const isCurrentQuestion =
                index === messages.length - 1 &&
                message.type === "send" &&
                index > 0;

              return (
                <ChatSend
                  key={`send-${index}`}
                  username="Empowered Man"
                  withtargets={isSecondSend}
                  targets={targets}
                  title={message.question}
                  time={message.time}
                  botImage="/icons/journal-brave.svg"
                  setIsThinkingLoading={setIsThinkingLoading}
                  isThinkingLoading={isThinkingLoading}
                  isCurrentQuestion={isCurrentQuestion}
                />
              );
            } else {
              return (
                <ChatReceive
                  key={`receive-${index}`}
                  response={message.response}
                  responder={message.responder}
                  time={message.time}
                  userImage={message.userImage}
                  answerId={message.answerId}
                  questionType={message.questionType}
                  isEditing={editingQuestion?.answerId === message.answerId}
                  onEdit={handleStartEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  disableEdit={message.questionType === "yes_no"}
                  onShowYesNoModal={handleStartEdit}
                />
              );
            }
          })}

          {/* Show begin button only if no answers exist yet */}
          {!isJournalStarted && currentQuestion && (
            <button
              className="text-body-b3 btn-1 btn-1-primary"
              id="body-journal-button"
              onClick={handleJournalingPress}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "240px",
                height: "52px",
                cursor: "pointer",
                alignSelf: "center",
                flexShrink: "0",
                padding: "0 12px",
                overflow: "hidden",
              }}
              title={`${userGoal.goal?.title || "Goal"}`}
            >
              <span
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "calc(100% - 85px)",
                }}
              >
                {userGoal.goal?.title?.replace(
                  /^(\w)(\w*)/,
                  (_, first, rest) => first.toUpperCase() + rest.toLowerCase(),
                ) || "Goal"}{" "}
              </span>
              Journaling
            </button>
          )}
        </div>

        {/* Message Box - Show when there's a current question and not yes/no and not showing modal */}
        {!showYesNoModal &&
          !allQuestionsAnswered &&
          isJournalStarted &&
          (currentQuestion || editingQuestion) &&
          (editingQuestion?.isYesNo ? false : true) && (
            <MessageBox
              key={
                editingQuestion
                  ? `editing-${editingQuestion.answerId}`
                  : "normal"
              }
              onSendClick={handleSendMessage}
              initialText={editingQuestion?.currentText || ""}
              isEditing={!!editingQuestion}
              setEditingMessage={setEditingQuestion}
              onCancelEdit={handleCancelEdit}
              isMessageBoxDisabled={messageSending}
              userProfile={userData}
              draftScopeId={tentComposerDraftScopeId}
            />
          )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="page-content">
        {userGoalLoading ? (
          <div className="text-center py-5">
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          </div>
        ) : (
          userGoal && (
            <div className="journal-chat-content">
              <div
                className="mb-3 mb-md-4"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h1
                  className="text-heading-h2 mb-0"
                  style={{
                    color: "#e6ecf1",
                  }}
                >
                  {userGoal.goal?.title?.replace(
                    /^(\w)(\w*)/,
                    (_, first, rest) =>
                      first.toUpperCase() + rest.toLowerCase(),
                  ) || "Goal"}{" "}
                  Tent
                </h1>
              </div>

              {noQuestions ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <p
                    className="text-center text-body-b2"
                    style={{ color: "#ccc" }}
                  >
                    No questions available.
                  </p>
                </div>
              ) : (
                renderChat()
              )}
            </div>
          )
        )}
      </div>

      {/* Yes/No Question Modal */}

      {showYesNoModal && currentYesNoQuestion && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="notCompletedJournalModal"
          tabIndex="-1"
          aria-labelledby="notCompletedJournalModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <h1
                  className="modal-title"
                  id="notCompletedJournalModalLabel"
                ></h1>
              </div>

              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-wrap"
                  style={{ fontSize: "19px" }}
                >
                  {currentYesNoQuestion.question}
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", height: "auto" }}
                    onClick={() => handleYesNoResponse(true)}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px", height: "auto" }}
                    onClick={() => handleYesNoResponse(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
